import React, { useState, useEffect, useCallback, useRef, useId } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { RichTextEditor } from "../components/ui/rich-text-editor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../components/ui/popover";
import { cn } from "../lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import DetailedWorkoutFields, {
  makeDefaultWorkoutFields,
  makeDefaultSet,
  WORKOUT_METRICS,
  DIFFICULTIES,
} from "../components/workouts/DetailedWorkoutFields";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Dumbbell,
  FolderOpen,
  Tag,
  X,
  ImageIcon,
  Upload,
  ChevronDown,
  Filter,
  Users,
  User,
  Loader2,
  Lock,
  Pencil,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  FileText,
  Youtube,
  ExternalLink,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia("(min-width: 640px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function AllWorkouts() {
  const { hasPermission } = useAuth();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "workouts";
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await api.get("/workouts/templates");
      setTemplates(res.data.templates || []);
    } catch {
      toast.error("Failed to fetch templates");
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/workouts/templates/${templateId}`);
      toast.success("Template deleted successfully");
      fetchTemplates();
    } catch {
      toast.error("Failed to delete template");
    }
  };

  useEffect(() => {
    if (activeTab === "templates") {
      fetchTemplates();
    }
  }, [activeTab, fetchTemplates]);

  const canView = hasPermission("view_workouts");
  const canAdd = hasPermission("add_workout");
  const canEdit = hasPermission("edit_workout");
  const canDelete = hasPermission("delete_workout");

  const [workouts, setWorkouts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("all");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [filterMetric, setFilterMetric] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // staged filter state — committed on Apply
  const [stagedSearch, setStagedSearch] = useState("");
  const [stagedFilterGroup, setStagedFilterGroup] = useState("all");
  const [stagedFilterDifficulty, setStagedFilterDifficulty] = useState("all");
  const [stagedFilterMetric, setStagedFilterMetric] = useState("all");
  const [stagedFilterType, setStagedFilterType] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [formDataList, setFormDataList] = useState([
    {
      title: "",
      groupName: "",
      groupIds: [],
      workoutType: "",
      typeIds: [],
      attachments: [],
      youtubeLink: "",
      instructions: "",
      presets: [
        {
          workoutMetric: "reps",
          difficulty: "beginner",
          sets: 1,
          setDetails: [makeDefaultSet(0)],
        }
      ]
    },
  ]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteWorkout, setDeleteWorkout] = useState(null);
  const [assignedMembersWorkout, setAssignedMembersWorkout] = useState(null);

  const [activeDropdownKey, setActiveDropdownKey] = useState(null);
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [typeSearchResults, setTypeSearchResults] = useState([]);

  // Presets management in expanded row
  const [expandedPresets, setExpandedPresets] = useState([]);
  const [expandedPresetsLoading, setExpandedPresetsLoading] = useState(false);
  const [isAddingPreset, setIsAddingPreset] = useState(false);
  const [newPreset, setNewPreset] = useState({
    workoutMetric: "reps",
    difficulty: "beginner",
    sets: 1,
    setDetails: [makeDefaultSet(0)],
  });
  const [editingPreset, setEditingPreset] = useState(null);
  const [deletePresetId, setDeletePresetId] = useState(null);

  const reloadExpandedPresets = useCallback(async (workoutId) => {
    if (!workoutId) return;
    try {
      const res = await api.get(`/workouts/presets?workoutId=${workoutId}`);
      setExpandedPresets(res.data.presets || []);
    } catch {
      toast.error("Failed to refresh presets");
    }
  }, []);

  useEffect(() => {
    if (expandedRowId) {
      setExpandedPresetsLoading(true);
      setIsAddingPreset(false);
      setEditingPreset(null);
      api
        .get(`/workouts/presets?workoutId=${expandedRowId}`)
        .then((res) => {
          setExpandedPresets(res.data.presets || []);
        })
        .catch(() => {
          toast.error("Failed to fetch presets for this workout");
        })
        .finally(() => {
          setExpandedPresetsLoading(false);
        });
    } else {
      setExpandedPresets([]);
    }
  }, [expandedRowId]);

  const groupSearchTimeoutRef = useRef(null);
  const typeSearchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!e.target.closest(".search-container")) {
        setActiveDropdownKey(null);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  useEffect(() => {
    if (canView) fetchMeta();
    else setLoading(false);
  }, [canView]);

  const fetchMeta = async () => {
    try {
      const [groupsRes, typesRes] = await Promise.all([
        api.get("/workouts/groups"),
        api.get("/workouts/types"),
      ]);
      setGroups(groupsRes.data.groups || []);
      setTypes(typesRes.data.types || []);
    } catch {
      toast.error("Failed to load filter options");
    }
  };

  const fetchWorkouts = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (search) params.append("search", search);
      if (filterGroup && filterGroup !== "all")
        params.append("groupId", filterGroup);
      if (filterDifficulty && filterDifficulty !== "all")
        params.append("difficulty", filterDifficulty);
      if (filterMetric && filterMetric !== "all")
        params.append("workoutMetric", filterMetric);
      if (filterType && filterType !== "all")
        params.append("typeId", filterType);
      const res = await api.get(`/workouts?${params.toString()}`);
      setWorkouts(res.data.workouts || []);
      setTotal(res.data.total ?? res.data.workouts?.length ?? 0);
    } catch {
      toast.error("Failed to fetch workouts");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    search,
    filterGroup,
    filterDifficulty,
    filterMetric,
    filterType,
    canView,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => fetchWorkouts(), 300);
    return () => clearTimeout(timer);
  }, [fetchWorkouts]);

  // sync staged state when sheet opens
  const handleOpenFilters = () => {
    setStagedSearch(search);
    setStagedFilterGroup(filterGroup);
    setStagedFilterDifficulty(filterDifficulty);
    setStagedFilterMetric(filterMetric);
    setStagedFilterType(filterType);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setSearch(stagedSearch);
    setFilterGroup(stagedFilterGroup);
    setFilterDifficulty(stagedFilterDifficulty);
    setFilterMetric(stagedFilterMetric);
    setFilterType(stagedFilterType);
    setPage(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setStagedSearch("");
    setStagedFilterGroup("all");
    setStagedFilterDifficulty("all");
    setStagedFilterMetric("all");
    setStagedFilterType("all");
    setSearch("");
    setFilterGroup("all");
    setFilterDifficulty("all");
    setFilterMetric("all");
    setFilterType("all");
    setPage(1);
    setShowFilters(false);
  };

  const handleOpenModal = (workout = null) => {
    if (workout) {
      setEditingWorkout(workout);
      const initialGroupName =
        workout.groupIds && workout.groupIds.length > 0
          ? groups.find((g) => g.id === workout.groupIds[0])?.name || ""
          : "";
      const initialTypeName =
        workout.typeIds && workout.typeIds.length > 0
          ? types.find((t) => t.id === workout.typeIds[0])?.name || ""
          : "";
      setFormDataList([
        {
          title: workout.title || "",
          groupName: initialGroupName,
          workoutType: initialTypeName,
          image: workout.image || "",
          groupIds: workout.groupIds || [],
          typeIds: workout.typeIds || [],
          difficulty: workout.difficulty || "beginner",
          sets: workout.sets || 1,
          setDetails: workout.setDetails || [],
          attachments: workout.attachments || [],
          youtubeLink: workout.youtubeLink || "",
          instructions: workout.instructions || "",
        },
      ]);
    } else {
      setEditingWorkout(null);
      setFormDataList([
        {
          title: "",
          groupName: "",
          groupIds: [],
          workoutType: "",
          typeIds: [],
          attachments: [],
          youtubeLink: "",
          instructions: "",
          presets: [
            {
              workoutMetric: "reps",
              difficulty: "beginner",
              sets: 1,
              setDetails: [makeDefaultSet(0)],
            }
          ]
        },
      ]);
    }
    setShowModal(true);
  };

  const handleDuplicateWorkout = (workout) => {
    setEditingWorkout(null);
    const initialGroupName =
      workout.groupIds && workout.groupIds.length > 0
        ? groups.find((g) => g.id === workout.groupIds[0])?.name || ""
        : "";
    const initialTypeName =
      workout.typeIds && workout.typeIds.length > 0
        ? types.find((t) => t.id === workout.typeIds[0])?.name || ""
        : "";
    setFormDataList([
      {
        title: workout.title ? `${workout.title} (Copy)` : "",
        groupName: initialGroupName,
        workoutType: initialTypeName,
        image: workout.image || "",
        groupIds: workout.groupIds || [],
        typeIds: workout.typeIds || [],
        attachments: workout.attachments || [],
        youtubeLink: workout.youtubeLink || "",
        instructions: workout.instructions || "",
        presets: [
          {
            workoutMetric: workout.workoutMetric || "reps",
            difficulty: workout.difficulty || "beginner",
            sets: workout.sets || 1,
            setDetails: workout.setDetails || [makeDefaultSet(0)],
          }
        ]
      },
    ]);
    setShowModal(true);
  };

  const addWorkoutCard = () => {
    setFormDataList((prev) => [
      ...prev,
      {
        title: "",
        groupName: "",
        groupIds: [],
        workoutType: "",
        typeIds: [],
        attachments: [],
        youtubeLink: "",
        instructions: "",
        presets: [
          {
            workoutMetric: "reps",
            difficulty: "beginner",
            sets: 1,
            setDetails: [makeDefaultSet(0)],
          }
        ]
      },
    ]);
  };

  const removeWorkoutCard = (index) => {
    setFormDataList((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateWorkoutCard = (index, nextFields) => {
    setFormDataList((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, ...nextFields } : item,
      ),
    );
  };

  const duplicateWorkoutCard = (index) => {
    setFormDataList((prev) => {
      const target = prev[index];
      const clone = JSON.parse(JSON.stringify(target));
      const nextList = [...prev];
      nextList.splice(index + 1, 0, clone);
      return nextList;
    });
  };

  const handleGroupSearch = (index, text) => {
    updateWorkoutCard(index, { groupName: text });
    setActiveDropdownKey(`group-${index}`);

    if (groupSearchTimeoutRef.current)
      clearTimeout(groupSearchTimeoutRef.current);
    groupSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/groups?search=${encodeURIComponent(text)}`,
        );
        setGroupSearchResults(res.data.groups || []);
      } catch (err) {
        console.error(err);
      }
    }, 300);
  };

  const handleGroupFocus = (index, text) => {
    setActiveDropdownKey(`group-${index}`);

    if (groupSearchTimeoutRef.current)
      clearTimeout(groupSearchTimeoutRef.current);
    groupSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/groups?search=${encodeURIComponent(text)}`,
        );
        setGroupSearchResults(res.data.groups || []);
      } catch (err) {
        console.error(err);
      }
    }, 100);
  };

  const handleTypeSearch = (index, text) => {
    updateWorkoutCard(index, { workoutType: text });
    setActiveDropdownKey(`type-${index}`);

    if (typeSearchTimeoutRef.current)
      clearTimeout(typeSearchTimeoutRef.current);
    typeSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/types?search=${encodeURIComponent(text)}`,
        );
        setTypeSearchResults(res.data.types || []);
      } catch (err) {
        console.error(err);
      }
    }, 300);
  };

  const handleTypeFocus = (index, text) => {
    setActiveDropdownKey(`type-${index}`);

    if (typeSearchTimeoutRef.current)
      clearTimeout(typeSearchTimeoutRef.current);
    typeSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/types?search=${encodeURIComponent(text)}`,
        );
        setTypeSearchResults(res.data.types || []);
      } catch (err) {
        console.error(err);
      }
    }, 100);
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormDataList((prev) =>
        prev.map((item, idx) =>
          idx === index ? { ...item, image: res.data.url } : item,
        ),
      );
      toast.success("Image uploaded");
    } catch {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const resolveGroupId = async (groupName) => {
    const trimmed = groupName.trim();
    if (!trimmed) return [];

    const existing = groups.find(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      return [existing.id];
    }

    try {
      const res = await api.post("/workouts/groups", { name: trimmed });
      const newGroup = res.data.group;
      setGroups((prev) => [...prev, newGroup]);
      return [newGroup.id];
    } catch (err) {
      console.error("Failed to create group on the fly:", err);
      toast.error(`Failed to create group "${trimmed}"`);
      throw err;
    }
  };

  const resolveTypeId = async (typeName) => {
    const trimmed = typeName.trim();
    if (!trimmed) return [];

    const existing = types.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      return [existing.id];
    }

    try {
      const res = await api.post("/workouts/types", { name: trimmed });
      const newType = res.data.type;
      setTypes((prev) => [...prev, newType]);
      return [newType.id];
    } catch (err) {
      console.error("Failed to create type on the fly:", err);
      toast.error(`Failed to create type "${trimmed}"`);
      throw err;
    }
  };

  const handleSubmit = async () => {
    for (let i = 0; i < formDataList.length; i++) {
      if (!formDataList[i].title.trim()) {
        toast.error(`Title is required for Workout #${i + 1}`);
        return;
      }
    }
    setSaving(true);
    try {
      const resolvedList = await Promise.all(
        formDataList.map(async (item) => {
          const groupIds = await resolveGroupId(item.groupName || "");
          const typeIds = await resolveTypeId(item.workoutType || "");
          
          if (!editingWorkout) {
            const firstPreset = item.presets && item.presets[0] ? item.presets[0] : {
              sets: 1,
              setDetails: [makeDefaultSet(0)],
              workoutMetric: "reps",
              difficulty: "beginner"
            };
            return {
              ...item,
              groupIds,
              typeIds,
              sets: firstPreset.sets,
              setDetails: firstPreset.setDetails,
              workoutMetric: firstPreset.workoutMetric,
              difficulty: firstPreset.difficulty
            };
          } else {
            return {
              ...item,
              groupIds,
              typeIds,
            };
          }
        }),
      );

      if (editingWorkout) {
        await api.put(`/workouts/${editingWorkout.id}`, resolvedList[0]);
        toast.success("Workout updated");
      } else {
        await Promise.all(
          resolvedList.map((item) => api.post("/workouts", item)),
        );
        toast.success(
          resolvedList.length > 1
            ? `${resolvedList.length} workouts created`
            : "Workout created",
        );
      }
      setShowModal(false);
      fetchWorkouts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save workout");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteWorkout) return;
    try {
      await api.delete(`/workouts/${deleteWorkout.id}`);
      toast.success("Workout deleted");
      setDeleteWorkout(null);
      fetchWorkouts();
    } catch {
      toast.error("Failed to delete workout");
    }
  };

  const toggleRow = (id) =>
    setExpandedRowId((prev) => (prev === id ? null : id));

  const renderWorkoutDetails = (workout) => {
    const isAttachmentImage = (file) => {
      if (file.type && file.type.startsWith("image/")) return true;
      const url = file.url || "";
      return /\.(jpg|jpeg|png|webp|gif|svg)/i.test(url);
    };

    const getYoutubeEmbedUrl = (url) => {
      if (!url) return null;
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}`;
      }
      return null;
    };

    const formatRest = (seconds) => {
      if (!seconds) return "";
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const rest = seconds % 60;
      return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
    };

    return (
      <div className="flex flex-col">
        {/* Action Bar */}
        <div className="flex items-center justify-end gap-2 p-2 border-b border-border bg-secondary/20">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => {
                e.stopPropagation();
                setAssignedMembersWorkout(workout);
              }}
              data-testid={`assigned-members-btn-${workout.id}`}
            >
              <Users className="h-3.5 w-3.5" />{" "}
              <span className="hidden md:block">Assigned Members</span>
            </Button>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(workout);
                }}
                data-testid={`edit-workout-${workout.id}`}
              >
                <Edit2 className="h-3.5 w-3.5" />{" "}
                <span className="hidden md:block">Edit Workout</span>
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteWorkout(workout);
                }}
                data-testid={`delete-workout-${workout.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />{" "}
                <span className="hidden md:block">Delete Workout</span>
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
          {/* Left Column: Workout Details */}
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                Safety / Workout Instructions
              </h4>
              {workout.instructions ? (
                <p className="text-sm text-foreground bg-secondary/20 p-3 rounded-lg border border-border/50 italic whitespace-pre-wrap">
                  {workout.instructions}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">No instructions provided.</p>
              )}
            </div>

            {workout.youtubeLink && (
              <div>
                <h4 className="text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Video Demonstration
                </h4>
                {getYoutubeEmbedUrl(workout.youtubeLink) ? (
                  <div className="aspect-video w-full max-w-md rounded-lg overflow-hidden border border-border bg-black">
                    <iframe
                      className="w-full h-full"
                      src={getYoutubeEmbedUrl(workout.youtubeLink)}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (
                  <a
                    href={workout.youtubeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-medium text-red-500 hover:text-red-600 bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 px-3 py-2 rounded-lg transition-all"
                  >
                    <Youtube className="h-4 w-4" /> Watch YouTube Video
                  </a>
                )}
              </div>
            )}

            <div>
              <h4 className="text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                Attachments
              </h4>
              {workout.attachments && workout.attachments.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {workout.attachments.map((file, idx) => {
                    const fileIsImage = isAttachmentImage(file);
                    const fileUrl = file.url.startsWith("http")
                      ? file.url
                      : `${BACKEND_URL}${file.url}`;
                    return (
                      <div
                        key={file.url || idx}
                        className="relative group w-12 h-12 border border-border rounded-lg bg-secondary/30 flex items-center justify-center cursor-pointer overflow-hidden transition-all hover:border-primary/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(fileUrl, "_blank");
                        }}
                        title={file.name || "Attachment"}
                      >
                        {fileIsImage ? (
                          <img
                            src={fileUrl}
                            alt={file.name || "Attachment"}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center p-1 text-center">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-[8px] text-muted-foreground truncate w-10 mt-0.5">
                              {file.name || "File"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No attachments uploaded.</p>
              )}
            </div>
          </div>

          {/* Right Column: Preset Details */}
          <div className="space-y-4 lg:border-l lg:border-border lg:pl-6">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground">
                Preset Configurations
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsAddingPreset(true);
                  setNewPreset({
                    workoutMetric: "reps",
                    difficulty: "beginner",
                    sets: 1,
                    setDetails: [makeDefaultSet(0)],
                  });
                }}
                className="h-8 text-xs font-heading uppercase"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Preset
              </Button>
            </div>

            {expandedPresetsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                {expandedPresets.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-4 text-center">
                    No presets found. Add a preset configuration to make it available in the workout builder.
                  </p>
                ) : (
                  expandedPresets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-border/60 bg-secondary/10 hover:bg-secondary/20 transition-all"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-foreground">
                            {preset.presetName}
                          </span>
                          <Badge
                            variant="secondary"
                            className={cn(
                              "text-[9px] font-mono uppercase px-1 py-0 h-4",
                              preset.difficulty === "beginner" &&
                                "bg-green-500/10 text-green-500 border-green-500/20",
                              preset.difficulty === "intermediate" &&
                                "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
                              preset.difficulty === "advanced" &&
                                "bg-red-500/10 text-red-500 border-red-500/20"
                            )}
                          >
                            {preset.difficulty || "beginner"}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground font-mono">
                          Metric: <span className="capitalize">{preset.workoutMetric}</span> &bull; {preset.sets} sets
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => setEditingPreset(preset)}
                          title="Edit Preset"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10"
                          onClick={() => setDeletePresetId(preset.id)}
                          title="Delete Preset"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const activeFilterCount = [
    search,
    filterGroup !== "all" ? filterGroup : "",
    filterDifficulty !== "all" ? filterDifficulty : "",
    filterMetric !== "all" ? filterMetric : "",
    filterType !== "all" ? filterType : "",
  ].filter((v) => (Array.isArray(v) ? v.length > 0 : !!v)).length;

  const hasActiveFilters = activeFilterCount > 0;
  const showActionsCol = canEdit || canDelete;

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="all-workouts-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
            All Workouts
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage your workout library
          </p>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-base">Access Restricted</p>
              <p className="text-sm text-muted-foreground mt-1">
                You don't have permission to view Workouts.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statCards = [{ label: "TOTAL WORKOUTS", value: total, icon: Dumbbell }];

  return (
    <div
      className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3"
      data-testid="all-workouts-page"
    >
      {/* ── Header ── */}
      <div className="flex sm:flex-row sm:items-center justify-between gap-2 md:gap-4 ">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">
            All Workouts
          </h1>
        </div>
        <div className="flex gap-2">
          {activeTab === "workouts" ? (
            <>
              <Button
                variant={activeFilterCount > 0 ? "secondary" : "outline"}
                size="sm"
                onClick={handleOpenFilters}
                className="font-heading uppercase tracking-wide text-xs"
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4" />{" "}
                <span className="hidden sm:block">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              {canAdd && (
                <Button
                  size="sm"
                  onClick={() => handleOpenModal()}
                  className="font-heading uppercase tracking-wide text-xs"
                  data-testid="add-workout-btn"
                >
                  <Plus className="h-4 w-4 " />
                  <span className="">Add Workout</span>
                </Button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab("workouts")}
          className={cn(
            "px-4 py-2 text-sm font-heading font-black uppercase tracking-wider transition-all border-b-2 -mb-px",
            activeTab === "workouts"
              ? "border-primary text-foreground font-extrabold"
              : "border-transparent text-muted-foreground hover:text-foreground font-medium"
          )}
        >
          Workouts
        </button>
        <button
          onClick={() => setActiveTab("templates")}
          className={cn(
            "px-4 py-2 text-sm font-heading font-black uppercase tracking-wider transition-all border-b-2 -mb-px",
            activeTab === "templates"
              ? "border-primary text-foreground font-extrabold"
              : "border-transparent text-muted-foreground hover:text-foreground font-medium"
          )}
        >
          Templates
        </button>
      </div>

      {/* ── Stats ── */}
      {activeTab === "workouts" ? (
        <div className="flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-3 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {statCards.map((s, i) => (
            <div className="flex items-center gap-2" key={i}>
              <p className="text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5 shrink-0">
                Total {s.value}
              </p>
              <div className="relative mt-2 max-w-xs md:max-w-md w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search workouts..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setStagedSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9 h-9 w-full bg-background"
                  data-testid="main-workout-search"
                />
                {search && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setSearch("");
                      setStagedSearch("");
                      setPage(1);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <p className="text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5 shrink-0">
            Total {templates.length} Templates
          </p>
        </div>
      )}

      {/* ── Filter Sheet — single instance, side driven by JS media query ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={
            isDesktop
              ? "w-[400px] flex flex-col p-0 bg-card border-border"
              : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
          }
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">
                    {activeFilterCount} active
                  </Badge>
                )}
              </SheetTitle>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-muted-foreground text-xs h-7"
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear all
                </Button>
              )}
            </div>
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Search */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search workouts..."
                  value={stagedSearch}
                  onChange={(e) => setStagedSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                  className="pl-9"
                  data-testid="workout-search"
                />
              </div>
            </div>

            <Separator />

            {/* Group, Type, Difficulty, and Metric filters */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Group
                  </Label>
                  <Select
                    value={stagedFilterGroup}
                    onValueChange={setStagedFilterGroup}
                  >
                    <SelectTrigger data-testid="filter-group">
                      <SelectValue placeholder="All Groups" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Groups</SelectItem>
                      {groups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Workout Type
                  </Label>
                  <Select
                    value={stagedFilterType}
                    onValueChange={setStagedFilterType}
                  >
                    <SelectTrigger data-testid="filter-type">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {types.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Difficulty
                  </Label>
                  <Select
                    value={stagedFilterDifficulty}
                    onValueChange={setStagedFilterDifficulty}
                  >
                    <SelectTrigger data-testid="filter-difficulty">
                      <SelectValue placeholder="All Difficulties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Difficulties</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Workout Metric
                  </Label>
                  <Select
                    value={stagedFilterMetric}
                    onValueChange={setStagedFilterMetric}
                  >
                    <SelectTrigger data-testid="filter-metric">
                      <SelectValue placeholder="All Metrics" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Metrics</SelectItem>
                      <SelectItem value="reps">Reps</SelectItem>
                      <SelectItem value="duration">Duration</SelectItem>
                      <SelectItem value="distance">Distance</SelectItem>
                      <SelectItem value="count">Count</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-card shrink-0">
            <Button
              className="w-full font-heading uppercase tracking-wide text-xs"
              onClick={handleApplyFilters}
            >
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Table + Mobile Cards ── */}
      {activeTab === "workouts" ? (
        <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0  md:mx-0 rounded-lg">
        {/* Desktop table */}
        <div className="hidden md:block overflow-auto flex-1">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead>Types</TableHead>
                <TableHead>No. of Presets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-12"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                      <span className="text-muted-foreground text-sm">
                        Loading...
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : workouts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-12"
                  >
                    <Dumbbell className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {hasActiveFilters
                        ? "No workouts match your filters."
                        : "Get started by adding your first workout."}
                    </p>
                    {!hasActiveFilters && canAdd && (
                      <Button
                        onClick={() => handleOpenModal()}
                        className="mt-4"
                        data-testid="empty-add-workout-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Workout
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                workouts.map((workout) => (
                  <React.Fragment key={workout.id}>
                    <TableRow
                      className="group cursor-pointer hover:bg-secondary/50"
                      data-testid={`workout-row-${workout.id}`}
                      onClick={() => toggleRow(workout.id)}
                    >
                      <TableCell className="w-10">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(workout.id);
                          }}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${expandedRowId === workout.id ? "rotate-180" : ""}`}
                          />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-sm">
                              {workout.title}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {workout.groupIds &&
                            workout.groupIds.slice(0, 2).map((id) => {
                              const g = groups.find((x) => x.id === id);
                              if (!g) return null;
                              return (
                                <Badge
                                  key={id}
                                  variant="outline"
                                  className="text-[10px] uppercase font-mono px-1.5 py-0 h-4 bg-blue-500/10 text-blue-500 border border-blue-500/20"
                                >
                                  {g.name}
                                </Badge>
                              );
                            })}
                          {workout.groupIds && workout.groupIds.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-[10px] uppercase font-mono px-1.5 py-0 h-4"
                            >
                              +{workout.groupIds.length - 2} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {workout.typeIds &&
                            workout.typeIds.slice(0, 2).map((id) => {
                              const t = types.find((x) => x.id === id);
                              if (!t) return null;
                              return (
                                <Badge
                                  key={id}
                                  variant="outline"
                                  className="text-[10px] uppercase font-mono px-1.5 py-0 h-4 bg-purple-500/10 text-purple-500 border border-purple-500/20"
                                >
                                  {t.name}
                                </Badge>
                              );
                            })}
                          {workout.typeIds && workout.typeIds.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-[10px] font-mono px-1.5 py-0 h-4"
                            >
                              +{workout.typeIds.length - 2} more
                            </Badge>
                          )}
                          {(!workout.typeIds ||
                            workout.typeIds.length === 0) && (
                            <span className="text-sm text-muted-foreground italic">
                              None
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono font-medium">
                          {workout.presetsCount || 0}
                        </span>
                      </TableCell>
                    </TableRow>
                    {expandedRowId === workout.id && (
                      <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border">
                        <TableCell colSpan={5} className="p-0">
                          {renderWorkoutDetails(workout)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* ── Mobile Card List ── */}
        <div className="md:hidden overflow-x-hidden overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
              <span className="text-muted-foreground text-sm ml-2">
                Loading...
              </span>
            </div>
          ) : workouts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No workouts found
            </div>
          ) : (
            workouts.map((workout, idx) => (
              <Collapsible
                key={workout.id}
                open={expandedRowId === workout.id}
                onOpenChange={() => toggleRow(workout.id)}
                asChild
              >
                <div
                  data-testid={`workout-card-${workout.id}`}
                  className={` border-b border-border hover:bg-secondary/10 transition-colors`}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-2 py-2">
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <div className="flex items-center justify-between flex-col shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0 animate-none"
                          >
                            <ChevronDown
                              className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === workout.id ? "rotate-180" : ""}`}
                            />
                          </Button>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm leading-tight text-foreground truncate">
                            {workout.title}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1 items-center">
                            {workout.groupIds &&
                              workout.groupIds.slice(0, 1).map((id) => {
                                const g = groups.find((x) => x.id === id);
                                if (!g) return null;
                                return (
                                  <Badge
                                    key={id}
                                    variant="outline"
                                    className="text-[9px] font-mono px-1.5 py-0 h-4 bg-blue-500/10 text-blue-500 border border-blue-500/20 uppercase"
                                  >
                                    {g.name}
                                  </Badge>
                                );
                              })}
                            {workout.groupIds &&
                              workout.groupIds.length > 1 && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] font-mono px-1.5 py-0 h-4"
                                >
                                  +{workout.groupIds.length - 1}
                                </Badge>
                              )}
                            {workout.typeIds &&
                              workout.typeIds.slice(0, 1).map((id) => {
                                const t = types.find((x) => x.id === id);
                                if (!t) return null;
                                return (
                                  <Badge
                                    key={id}
                                    variant="outline"
                                    className="text-[9px] font-mono px-1.5 py-0 h-4 bg-purple-500/10 text-purple-500 border border-purple-500/20 uppercase"
                                  >
                                    {t.name}
                                  </Badge>
                                );
                              })}
                            {workout.typeIds && workout.typeIds.length > 1 && (
                              <Badge
                                variant="outline"
                                className="text-[9px] font-mono px-1.5 py-0 h-4"
                              >
                                +{workout.typeIds.length - 1}
                              </Badge>
                            )}
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-[9px] font-mono uppercase px-1 py-0 h-4",
                                workout.difficulty === "beginner" &&
                                  "bg-green-500/10 text-green-500 border-green-500/20",
                                workout.difficulty === "intermediate" &&
                                  "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
                                workout.difficulty === "advanced" &&
                                  "bg-red-500/10 text-red-500 border-red-500/20",
                              )}
                            >
                              {workout.difficulty || "beginner"}
                            </Badge>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1.5 font-mono">
                            Presets:{" "}
                            <span className="font-semibold text-foreground">
                              {workout.presetsCount || 0}
                            </span>
                          </p>
                        </div>
                      </div>
                      {canAdd && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateWorkout(workout);
                            }}
                            title="Duplicate Workout"
                            data-testid={`duplicate-workout-mobile-${workout.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className=" border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                      {renderWorkoutDetails(workout)}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))
          )}
        </div>

        {/* ── Pagination ── */}
        <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono hidden sm:block">{total} total</span>
            <div className="flex items-center gap-2">
              <span className="text-xs">Show:</span>
              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  setLimit(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft />
            </Button>
            <span className="sm:text-sm text-xs text-muted-foreground px-2">
              Page {page} of {Math.ceil(total / limit) || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      </Card>
      ) : (
        <div className="space-y-4 mt-2">
          {templatesLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : templates.length === 0 ? (
            <Card className="bg-card border border-border shadow-sm p-8 text-center text-muted-foreground rounded-lg">
              <FolderOpen className="h-10 w-10 mx-auto opacity-30 mb-3" />
              <p className="text-sm font-medium">No templates saved yet.</p>
              <p className="text-xs mt-1">
                You can save a template while assigning a workout card to a member.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="bg-card border border-border shadow-sm flex flex-col justify-between hover:border-primary/40 transition-all rounded-lg overflow-hidden">
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-heading text-base font-bold uppercase text-foreground leading-tight">
                          {template.name}
                        </h3>
                        <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                          Card: {template.cardName || template.name}
                        </p>
                      </div>
                      <Badge className="bg-primary/10 text-primary border border-primary/20 text-[9px] uppercase shrink-0 font-heading">
                        {template.numberOfWeeks} Weeks
                      </Badge>
                    </div>

                    {template.notes && (
                      <p className="text-xs text-muted-foreground/80 line-clamp-2 italic">
                        "{template.notes}"
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/40 text-[10px] font-mono text-muted-foreground">
                      <div>
                        <span className="font-semibold text-foreground">Prep Days:</span>{" "}
                        {template.fitnessPreparationDays || 0}
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Re-prep Days:</span>{" "}
                        {template.fitnessRePreparationDays || 0}
                      </div>
                      <div className="col-span-2">
                        <span className="font-semibold text-foreground">Rest Days:</span>{" "}
                        {template.restDays?.map(d => d.substring(0, 3)).join(", ") || "None"}
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-secondary/20 border-t border-border/40 flex items-center justify-end gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/members/template/workouts/new?templateId=${template.id}`)}
                      className="h-7 px-2.5 text-[10px] font-heading uppercase tracking-wide"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="h-7 px-2.5 text-[10px] font-heading uppercase tracking-wide border-red-500/20 text-red-500 hover:bg-red-500/10 hover:text-red-500"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Add/Edit Dialog ── */}
      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          if (!saving) setShowModal(open);
        }}
      >
        <DialogContent className="max-w-2xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
            <DialogTitle className="font-heading uppercase tracking-wide">
              {editingWorkout ? "Edit Workout" : "Add Workout"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <div className="space-y-4">
              {formDataList.map((workout, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-border bg-secondary/20 p-4 space-y-4 transition-all duration-200 relative"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-sm font-bold text-card-foreground">
                      Workout #{index + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      {!editingWorkout && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:bg-secondary/40"
                          onClick={() => duplicateWorkoutCard(index)}
                          disabled={saving}
                          title="Duplicate Workout"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                      {!editingWorkout && formDataList.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          onClick={() => removeWorkoutCard(index)}
                          disabled={saving}
                          title="Delete Workout"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Section 1: Title, Group, Type */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-heading uppercase">
                        Title *
                      </Label>
                      <Input
                        value={workout.title}
                        onChange={(e) =>
                          updateWorkoutCard(index, { title: e.target.value })
                        }
                        placeholder="Enter workout title"
                        disabled={saving}
                        data-testid={`workout-title-input-${index}`}
                      />
                    </div>

                    <div className="space-y-2 search-container relative">
                      <Label className="text-xs font-heading uppercase">
                        Workout Group
                      </Label>
                      <Input
                        value={workout.groupName || ""}
                        onChange={(e) =>
                          handleGroupSearch(index, e.target.value)
                        }
                        onFocus={(e) => handleGroupFocus(index, e.target.value)}
                        placeholder="Search or type group"
                        autoComplete="off"
                        disabled={saving}
                      />
                      {activeDropdownKey === `group-${index}` &&
                        groupSearchResults.length > 0 && (
                          <div className="absolute left-0 right-0 top-[calc(100%+4px)] bg-card border border-border rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto divide-y divide-border/40 scrollbar-thin">
                            {groupSearchResults.map((group) => (
                              <div
                                key={group.id}
                                className="p-2.5 hover:bg-secondary/40 cursor-pointer transition-colors text-left text-sm text-foreground truncate bg-card"
                                onClick={() => {
                                  updateWorkoutCard(index, {
                                    groupName: group.name,
                                  });
                                  setActiveDropdownKey(null);
                                }}
                              >
                                {group.name}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    <div className="space-y-2 search-container relative">
                      <Label className="text-xs font-heading uppercase">
                        Workout Type
                      </Label>
                      <Input
                        value={workout.workoutType || ""}
                        onChange={(e) =>
                          handleTypeSearch(index, e.target.value)
                        }
                        onFocus={(e) => handleTypeFocus(index, e.target.value)}
                        placeholder="Search or type type"
                        autoComplete="off"
                        disabled={saving}
                      />
                      {activeDropdownKey === `type-${index}` &&
                        typeSearchResults.length > 0 && (
                          <div className="absolute left-0 right-0 top-[calc(100%+4px)] bg-card border border-border rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto divide-y divide-border/40 scrollbar-thin">
                            {typeSearchResults.map((type) => (
                              <div
                                key={type.id}
                                className="p-2.5 hover:bg-secondary/40 cursor-pointer transition-colors text-left text-sm text-foreground truncate bg-card"
                                onClick={() => {
                                  updateWorkoutCard(index, {
                                    workoutType: type.name,
                                  });
                                  setActiveDropdownKey(null);
                                }}
                              >
                                {type.name}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Section 2: Presets (Only during ADD mode, duplicate-able) */}
                  {!editingWorkout && (
                    <div className="space-y-4 border-t border-border/30 pt-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-heading uppercase text-muted-foreground font-bold tracking-wider">
                          Preset Configurations
                        </Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const updatedPresets = [
                              ...(workout.presets || []),
                              {
                                workoutMetric: "reps",
                                difficulty: "beginner",
                                sets: 1,
                                setDetails: [makeDefaultSet(0)],
                              },
                            ];
                            updateWorkoutCard(index, { presets: updatedPresets });
                          }}
                          className="h-8 text-xs font-heading uppercase"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Preset
                        </Button>
                      </div>

                      {(workout.presets || []).map((preset, presetIdx) => (
                        <PresetSectionEditor
                          key={presetIdx}
                          preset={preset}
                          index={presetIdx}
                          onChange={(updatedPreset) => {
                            const updatedPresets = workout.presets.map((p, pIdx) =>
                              pIdx === presetIdx ? updatedPreset : p
                            );
                            updateWorkoutCard(index, { presets: updatedPresets });
                          }}
                          onDuplicate={() => {
                            const clone = JSON.parse(JSON.stringify(preset));
                            const updatedPresets = [...workout.presets];
                            updatedPresets.splice(presetIdx + 1, 0, clone);
                            updateWorkoutCard(index, { presets: updatedPresets });
                          }}
                          onDelete={
                            workout.presets.length > 1
                              ? () => {
                                  const updatedPresets = workout.presets.filter(
                                    (_, pIdx) => pIdx !== presetIdx
                                  );
                                  updateWorkoutCard(index, { presets: updatedPresets });
                                }
                              : null
                          }
                        />
                      ))}
                    </div>
                  )}

                  {/* Section 3: Attachments, Youtube Link, Instructions */}
                  <div className="space-y-4 border-t border-border/30 pt-4">
                    <Label className="text-xs font-heading uppercase text-muted-foreground font-bold tracking-wider">
                      Media & Instructions
                    </Label>
                    <WorkoutMediaAndInstructions
                      workout={workout}
                      onChange={(next) => updateWorkoutCard(index, next)}
                      disabled={saving}
                    />
                  </div>
                </div>
              ))}
            </div>

            {!editingWorkout && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed border-border hover:border-primary/50 flex items-center justify-center gap-1.5 h-10 transition-all duration-200"
                onClick={addWorkoutCard}
                disabled={saving}
              >
                <Plus className="h-4 w-4 text-muted-foreground" />
                Add Another Workout
              </Button>
            )}
          </div>
          <div className="px-6 py-4 border-t border-border shrink-0 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || uploading}
              data-testid="save-workout-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {editingWorkout ? "Saving..." : "Creating..."}
                </>
              ) : editingWorkout ? (
                "Save Changes"
              ) : (
                "Create Workout"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog
        open={!!deleteWorkout}
        onOpenChange={() => setDeleteWorkout(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Delete Workout
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteWorkout?.title}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-workout"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Preset Delete Confirmation ── */}
      <AlertDialog
        open={!!deletePresetId}
        onOpenChange={() => setDeletePresetId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Delete Preset
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this preset configuration? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deletePresetId) return;
                try {
                  await api.delete(`/workouts/presets/${deletePresetId}`);
                  toast.success("Preset deleted");
                  setDeletePresetId(null);
                  reloadExpandedPresets(expandedRowId);
                  fetchWorkouts();
                } catch {
                  toast.error("Failed to delete preset");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Add/Edit Preset Dialog ── */}
      <Dialog
        open={isAddingPreset || !!editingPreset}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddingPreset(false);
            setEditingPreset(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
            <DialogTitle className="font-heading uppercase tracking-wide">
              {isAddingPreset ? "Add Preset" : "Edit Preset"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-thin">
            {isAddingPreset ? (
              <PresetSectionEditor
                preset={newPreset}
                onChange={setNewPreset}
                index={0}
              />
            ) : editingPreset ? (
              <PresetSectionEditor
                preset={editingPreset}
                onChange={setEditingPreset}
                index={0}
              />
            ) : null}
          </div>
          <div className="px-6 py-4 border-t border-border shrink-0 flex justify-end gap-2 bg-secondary/10">
            <Button
              variant="outline"
              onClick={() => {
                setIsAddingPreset(false);
                setEditingPreset(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (isAddingPreset) {
                  try {
                    await api.post("/workouts/presets", {
                      workoutId: expandedRowId,
                      sets: newPreset.sets,
                      setDetails: newPreset.setDetails,
                      workoutMetric: newPreset.workoutMetric,
                      difficulty: newPreset.difficulty,
                    });
                    toast.success("Preset added successfully");
                    setIsAddingPreset(false);
                    reloadExpandedPresets(expandedRowId);
                    fetchWorkouts();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to add preset");
                  }
                } else if (editingPreset) {
                  try {
                    await api.put(`/workouts/presets/${editingPreset.id}`, {
                      sets: editingPreset.sets,
                      setDetails: editingPreset.setDetails,
                      workoutMetric: editingPreset.workoutMetric,
                      difficulty: editingPreset.difficulty,
                    });
                    toast.success("Preset updated successfully");
                    setEditingPreset(null);
                    reloadExpandedPresets(expandedRowId);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to update preset");
                  }
                }
              }}
            >
              {isAddingPreset ? "Save Preset" : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Assigned Members Dialog ── */}
      <AssignedMembersDialog
        workout={assignedMembersWorkout}
        onClose={() => setAssignedMembersWorkout(null)}
      />
    </div>
  );
}

function AssignedMembersDialog({ workout, onClose }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workout) {
      setMembers([]);
      return;
    }
    setLoading(true);
    api
      .get(`/workouts/${workout.id}/assigned-members`)
      .then((res) => setMembers(res.data.members || []))
      .catch(() => toast.error("Failed to load assigned members"))
      .finally(() => setLoading(false));
  }, [workout]);

  return (
    <Dialog
      open={!!workout}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-lg flex flex-col p-0 gap-0 overflow-hidden max-h-[80vh]">
        <DialogHeader className="px-6 py-5 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <Users className="h-4 w-4" />
            Assigned Members
          </DialogTitle>
          {workout && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {workout.title}
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-16">
              <User className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">No members assigned</p>
              <p className="text-xs text-muted-foreground mt-1">
                This workout hasn&apos;t been assigned to any member yet.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-4 px-6 py-3 hover:bg-secondary/30 transition-colors"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    {member.photo ? (
                      <AvatarImage
                        src={`${BACKEND_URL}${member.photo}`}
                        alt={member.name}
                      />
                    ) : null}
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {member.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {member.name}
                    </p>
                    {member.email && (
                      <p className="text-xs text-muted-foreground truncate">
                        {member.email}
                      </p>
                    )}
                  </div>
                  {member.assignedDate && (
                    <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                      {member.assignedDate}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {!loading && members.length > 0 && (
          <div className="px-6 py-3 border-t border-border shrink-0">
            <p className="text-xs text-muted-foreground font-mono">
              {members.length} member{members.length !== 1 ? "s" : ""} assigned
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Preset Section Editor ───────────────────────────────────────────────────────────
function PresetSectionEditor({ preset, onChange, onDuplicate, onDelete, index }) {
  const REST_OPTIONS = [
    10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180,
  ];

  const formatRest = (seconds) => {
    if (seconds < 60) return `${seconds} sec`;
    if (seconds === 60) return "60 sec";
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return rest ? `${minutes} min ${rest} sec` : `${minutes} min`;
  };

  const handleMetricChange = (workoutMetric) => {
    onChange({ ...preset, workoutMetric });
  };

  const handleDifficultyChange = (difficulty) => {
    onChange({ ...preset, difficulty });
  };

  const handleSetsChange = (raw) => {
    const sets = Number(raw);
    const setDetails = Array.from({ length: sets }, (_, idx) => ({
      ...makeDefaultSet(idx),
      ...(preset.setDetails[idx] || {}),
      setNumber: idx + 1,
    }));
    onChange({ ...preset, sets, setDetails });
  };

  const updateSet = (setIdx, patch) => {
    const next = preset.setDetails.map((set, idx) =>
      idx === setIdx ? { ...set, ...patch } : set
    );
    onChange({ ...preset, setDetails: next });
  };

  const duplicateSet = (setIdx) => {
    const setToDuplicate = preset.setDetails[setIdx];
    const nextDetails = [...preset.setDetails];
    nextDetails.splice(setIdx + 1, 0, {
      ...setToDuplicate,
      setNumber: setToDuplicate.setNumber + 1,
    });
    const updatedDetails = nextDetails.map((set, idx) => ({
      ...set,
      setNumber: idx + 1,
    }));
    onChange({
      ...preset,
      sets: preset.sets + 1,
      setDetails: updatedDetails,
    });
  };

  return (
    <div className="border border-border/80 bg-secondary/10 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-border/40">
        <span className="text-xs font-heading font-bold uppercase tracking-wider text-muted-foreground">
          Preset #{index + 1}
        </span>
        <div className="flex items-center gap-2">
          {onDuplicate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDuplicate}
              className="h-7 text-[10px] font-heading uppercase"
            >
              <Copy className="h-3 w-3 mr-1" /> Duplicate Preset
            </Button>
          )}
          {onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="h-7 text-[10px] font-heading uppercase text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">Workout Metric</Label>
          <Select value={preset.workoutMetric} onValueChange={handleMetricChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WORKOUT_METRICS.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">Difficulty</Label>
          <Select value={preset.difficulty} onValueChange={handleDifficultyChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIFFICULTIES.map((diff) => (
                <SelectItem key={diff.value} value={diff.value}>
                  {diff.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">Sets</Label>
          <Select value={String(preset.sets)} onValueChange={handleSetsChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 30 }, (_, idx) => idx + 1).map((cnt) => (
                <SelectItem key={cnt} value={String(cnt)}>
                  {cnt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-heading uppercase">Set Details</Label>
        
        {preset.setDetails.length > 0 && (
          <div className="hidden md:grid md:grid-cols-5 gap-2 p-2 border border-transparent text-[10px] font-heading uppercase text-muted-foreground font-semibold pb-0">
            <div></div>
            {preset.workoutMetric === "reps" ? (
              <>
                <div>Reps</div>
                <div>Weight</div>
                <div>Rest</div>
                <div></div>
              </>
            ) : (
              <>
                <div>
                  {preset.workoutMetric === "duration"
                    ? "Duration"
                    : preset.workoutMetric === "distance"
                      ? "Distance"
                      : "Count"}
                </div>
                <div>Rest</div>
                <div></div>
                <div></div>
              </>
            )}
          </div>
        )}

        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {preset.setDetails.map((set, setIdx) => (
            <div
              key={setIdx}
              className="grid grid-cols-2 md:grid-cols-5 gap-2 rounded-lg border border-border/60 bg-background/50 p-2"
            >
              <div className="flex items-center text-xs font-mono text-muted-foreground">
                Set {setIdx + 1}
              </div>
              {preset.workoutMetric === "reps" ? (
                <>
                  <Select
                    value={String(set.reps || "10")}
                    onValueChange={(reps) => updateSet(setIdx, { reps })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Reps" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 50 }, (_, oIdx) => oIdx + 1).map((cnt) => (
                        <SelectItem key={cnt} value={String(cnt)}>
                          {cnt} reps
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    value={set.weightKg || ""}
                    onChange={(e) => updateSet(setIdx, { weightKg: e.target.value })}
                    placeholder="Weight kg"
                  />
                  <Select
                    value={String(set.restSeconds !== undefined && set.restSeconds !== null ? set.restSeconds : "none")}
                    onValueChange={(restSeconds) =>
                      updateSet(setIdx, { restSeconds: restSeconds === "none" ? "none" : Number(restSeconds) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Rest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {REST_OPTIONS.map((seconds) => (
                        <SelectItem key={seconds} value={String(seconds)}>
                          {formatRest(seconds)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-end md:justify-center col-span-2 md:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary/40 rounded-sm"
                      onClick={() => duplicateSet(setIdx)}
                      title="Duplicate Set"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {preset.workoutMetric === "duration" && (
                    <Select
                      value={String(set.durationMin || "1")}
                      onValueChange={(durationMin) => updateSet(setIdx, { durationMin })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 50 }, (_, oIdx) => oIdx + 1).map((cnt) => (
                          <SelectItem key={cnt} value={String(cnt)}>
                            {cnt} min
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {preset.workoutMetric === "distance" && (
                    <Select
                      value={String(set.distanceKm || "1")}
                      onValueChange={(distanceKm) => updateSet(setIdx, { distanceKm })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Distance" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 50 }, (_, oIdx) => oIdx + 1).map((cnt) => (
                          <SelectItem key={cnt} value={String(cnt)}>
                            {cnt} km
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {preset.workoutMetric === "count" && (
                    <Select
                      value={String(set.count || "10")}
                      onValueChange={(count) => updateSet(setIdx, { count })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Count" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 100 }, (_, oIdx) => oIdx + 1).map((cnt) => (
                          <SelectItem key={cnt} value={String(cnt)}>
                            {cnt} counts
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select
                    value={String(set.restSeconds !== undefined && set.restSeconds !== null ? set.restSeconds : "none")}
                    onValueChange={(restSeconds) =>
                      updateSet(setIdx, { restSeconds: restSeconds === "none" ? "none" : Number(restSeconds) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Rest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {REST_OPTIONS.map((seconds) => (
                        <SelectItem key={seconds} value={String(seconds)}>
                          {formatRest(seconds)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-end md:justify-center col-span-2 md:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary/40 rounded-sm"
                      onClick={() => duplicateSet(setIdx)}
                      title="Duplicate Set"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="hidden md:block"></div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Workout Media & Instructions ───────────────────────────────────────────────────
function WorkoutMediaAndInstructions({ workout, onChange, disabled }) {
  const uploadId = useId();
  const [uploading, setUploading] = useState(false);

  const isImage = (file) => {
    if (file.type && file.type.startsWith("image/")) return true;
    const url = file.url || "";
    return /\.(jpg|jpeg|png|webp|gif|svg)/i.test(url);
  };

  const handleAttachmentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ url: res.data.url, name: file.name, type: file.type });
      }
      onChange({ ...workout, attachments: [...(workout.attachments || []), ...uploaded] });
      toast.success("Attachments uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload attachment");
    } finally {
      event.target.value = "";
      setUploading(false);
    }
  };

  const removeAttachment = (url) => {
    onChange({
      ...workout,
      attachments: (workout.attachments || []).filter((file) => file.url !== url),
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">
            Images / Video / PDF
          </Label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id={uploadId}
              type="file"
              multiple
              accept="image/*,video/*,application/pdf"
              className="hidden"
              onChange={handleAttachmentUpload}
              disabled={disabled || uploading}
            />
            <label htmlFor={uploadId} className="cursor-pointer">
              <div className="flex flex-col items-center justify-center w-14 h-14 border border-dashed border-border rounded-lg bg-secondary/10 hover:bg-secondary/20 transition-all gap-1 text-[10px] font-medium text-muted-foreground select-none">
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                <span>Upload</span>
              </div>
            </label>

            {(workout.attachments || []).map((file, idx) => {
              const fileIsImage = isImage(file);
              const fileUrl = file.url.startsWith("http")
                ? file.url
                : `${BACKEND_URL}${file.url}`;
              return (
                <div
                  key={file.url || idx}
                  className="relative group w-14 h-14 border border-border rounded-lg bg-secondary/20 flex items-center justify-center cursor-pointer overflow-hidden transition-all hover:border-primary/50"
                  onClick={() => {
                    if (fileIsImage) {
                      window.open(fileUrl, "_blank");
                    } else {
                      window.open(fileUrl, "_blank");
                    }
                  }}
                >
                  {fileIsImage ? (
                    <img
                      src={fileUrl}
                      alt={file.name || "Attachment"}
                      className="w-full h-full object-cover transition-all group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-1 text-center">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <span className="text-[8px] text-muted-foreground truncate w-12 mt-0.5">
                        {file.name || "File"}
                      </span>
                    </div>
                  )}

                  {!disabled && (
                    <button
                      type="button"
                      className="absolute top-1 right-0 rounded-full bg-destructive text-destructive-foreground p-0.5 shadow hover:bg-destructive/90 transition-colors opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAttachment(file.url);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">YouTube Link</Label>
          <Input
            value={workout.youtubeLink || ""}
            onChange={(e) => onChange({ ...workout, youtubeLink: e.target.value })}
            placeholder="https://youtube.com/..."
            disabled={disabled}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-heading uppercase">
          Workout / Safety Instructions
        </Label>
        <Textarea
          value={workout.instructions || ""}
          onChange={(e) => onChange({ ...workout, instructions: e.target.value })}
          placeholder="Instructions, cues, safety notes"
          rows={3}
          disabled={disabled}
          className="min-h-[80px]"
        />
      </div>
    </div>
  );
}

// ─── Workout Group picker ───────────────────────────────────────────────────────────
function WorkoutGroupPicker({
  groups,
  setGroups,
  selectedIds,
  onChange,
  placeholder = "Search or create...",
  createPlaceholder = "Create new group...",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [newText, setNewText] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const resolveChipStyle = (name) => {
    const colors = [
      {
        bg: "rgba(59, 130, 246, 0.1)",
        text: "#3b82f6",
        border: "1px solid rgba(59, 130, 246, 0.2)",
      },
      {
        bg: "rgba(99, 86, 255, 0.1)",
        text: "#6356ff",
        border: "1px solid rgba(99, 86, 255, 0.2)",
      },
      {
        bg: "rgba(16, 185, 129, 0.1)",
        text: "#10b981",
        border: "1px solid rgba(16, 185, 129, 0.2)",
      },
      {
        bg: "rgba(245, 158, 11, 0.1)",
        text: "#f59e0b",
        border: "1px solid rgba(245, 158, 11, 0.2)",
      },
      {
        bg: "rgba(236, 72, 153, 0.1)",
        text: "#ec4899",
        border: "1px solid rgba(236, 72, 153, 0.2)",
      },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const filtered = search.trim()
    ? groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()))
    : groups;

  const toggle = (id) =>
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );

  const handleCreate = async () => {
    const text = newText.trim();
    if (!text) return;
    if (groups.find((g) => g.name.toLowerCase() === text.toLowerCase())) {
      toast.error("Already exists");
      return;
    }
    try {
      const res = await api.post("/workouts/groups", { name: text });
      const newGroup = res.data.group;
      setGroups((prev) => [...prev, newGroup]);
      onChange([...selectedIds, newGroup.id]);
      setNewText("");
      setSearch("");
    } catch {
      toast.error("Failed to create workout group");
    }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    if (
      groups.find(
        (g) => g.name.toLowerCase() === text.toLowerCase() && g.id !== id,
      )
    ) {
      toast.error("Another group with this name already exists");
      return;
    }
    try {
      await api.put(`/workouts/groups/${id}`, { name: text });
      setGroups((prev) =>
        prev.map((g) => (g.id === id ? { ...g, name: text } : g)),
      );
      setEditingId(null);
      setEditText("");
      toast.success("Group updated successfully");
    } catch {
      toast.error("Failed to update workout group");
    }
  };

  const selectedObjs = groups.filter((g) => selectedIds.includes(g.id));
  const close = () => {
    setOpen(false);
    setSearch("");
    setEditingId(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={() => inputRef.current?.focus()}
          className={cn(
            "min-h-[42px] w-full px-3 py-1.5 rounded-lg border text-sm flex flex-wrap items-center gap-1.5 cursor-text transition-all bg-background border-input hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary",
            open && "ring-2 ring-primary/20 border-primary",
          )}
        >
          {selectedObjs.map((g) => {
            const c = resolveChipStyle(g.name);
            return (
              <span
                key={g.id}
                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: c.bg, color: c.text, border: c.border }}
              >
                <FolderOpen className="w-3.5 h-3.5 mr-0.5" />
                {g.name}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(g.id);
                  }}
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors ml-1"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={selectedIds.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[100px] bg-transparent outline-none text-foreground placeholder:text-muted-foreground text-sm py-0.5"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border-border rounded-xl overflow-hidden"
        align="start"
        style={{
          width: triggerWidth > 0 ? triggerWidth : "auto",
          minWidth: "240px",
        }}
      >
        <div className="p-3 border-b border-border space-y-2.5 bg-muted/20">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-input focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/25">
            <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups..."
              className="flex-1 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder={createPlaceholder}
              className="flex-1 h-8 px-3 text-xs rounded-lg border border-input bg-background outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-foreground placeholder:text-muted-foreground"
            />
            <Button
              type="button"
              onClick={handleCreate}
              disabled={!newText.trim()}
              className="h-8 px-3 rounded-lg text-xs font-semibold"
              size="sm"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[220px] p-1 space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nothing found &mdash; create one above
            </p>
          ) : (
            filtered.map((g) => {
              const isSel = selectedIds.includes(g.id);
              const c = resolveChipStyle(g.name);
              return (
                <div
                  key={g.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors group",
                    isSel ? "bg-primary/10" : "hover:bg-muted",
                  )}
                >
                  {editingId === g.id ? (
                    <input
                      autoFocus
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(g.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="flex-1 h-7 px-2 text-xs rounded border border-primary outline-none bg-background text-foreground"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggle(g.id)}
                      className="flex-1 text-left flex items-center gap-2"
                    >
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all"
                        style={{
                          background: c.bg,
                          color: c.text,
                          border: c.border,
                        }}
                      >
                        <FolderOpen className="w-3.5 h-3.5 mr-1" />
                        {g.name}
                      </span>
                    </button>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    {editingId === g.id ? (
                      <button
                        type="button"
                        onClick={() => saveEdit(g.id)}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-emerald-500/10"
                      >
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(g.id);
                          setEditText(g.name);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-muted-foreground/10"
                      >
                        <Pencil className="w-3.5 h-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {isSel && editingId !== g.id && (
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="px-3.5 py-2.5 border-t border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {selectedIds.length} selected
            </span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-destructive hover:text-destructive/80 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
